import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { ApiResponse, UserEffectivePermissions } from '../models/user-access.model';

// applicationId of "PrimeRx Configuration portal" in the permissions API
export const CONFIGURATION_PORTAL_APPLICATION_ID = 2;

@Injectable({
  providedIn: 'root'
})
export class UserAccessService {
  private cachedEmail: string | null = null;
  private cachedPermissions$: Observable<UserEffectivePermissions | null> | null = null;

  constructor(private http: HttpClient) { }

  // Emits null when the permissions service does not know the user (404) or refuses them (403).
  // Any other failure is rethrown so callers can tell "not allowed" apart from "could not check".
  getPermissions(email: string): Observable<UserEffectivePermissions | null> {
    const normalizedEmail = email.trim().toLowerCase();

    if (this.cachedPermissions$ && this.cachedEmail === normalizedEmail) {
      return this.cachedPermissions$;
    }

    const url = `${environment.configStoreApiUrl}/storeConfigStoreservice/configuration/users/${encodeURIComponent(normalizedEmail)}/permissions`;

    this.cachedEmail = normalizedEmail;
    this.cachedPermissions$ = this.http.get<ApiResponse<UserEffectivePermissions>>(url).pipe(
      map(response => response.data ?? null),
      catchError((err: HttpErrorResponse) =>
        err.status === 404 || err.status === 403 ? of(null) : throwError(() => err)
      ),
      shareReplay(1)
    );

    return this.cachedPermissions$;
  }

  // A user may sign in when they have read access to at least one resource of the configuration portal.
  canLogin(email: string | null): Observable<boolean> {
    if (!email) {
      return of(false);
    }

    return this.getPermissions(email).pipe(
      map(permissions => !!permissions?.applications.some(app =>
        app.applicationId === CONFIGURATION_PORTAL_APPLICATION_ID &&
        app.resourceNodes.some(node => node.read.effective)
      ))
    );
  }
}
